import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays, parse } from "date-fns";
import { de } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Clock,
  Phone,
  Users,
  CheckCircle2,
  AlertCircle,
  Download,
  RefreshCw,
} from "lucide-react";
import { isStaffLoggedIn, staffHeaders, getStaffPin } from "@/lib/staffAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDe = (d) => format(d, "dd.MM.yyyy");

const StaffTagesplan = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    if (!isStaffLoggedIn()) navigate("/staff/login", { replace: true });
  }, [navigate]);

  const dateStr = fmtDe(date);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/staff/tagesplan/${dateStr}`, {
        headers: staffHeaders(),
      });
      setData(r.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/staff/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [dateStr, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [fetchData]);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const downloadPdf = async () => {
    try {
      const r = await axios.get(`${API}/staff/tagesplan/${dateStr}/pdf`, {
        headers: staffHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Tagesplan-${dateStr.replace(/\./g, "-")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error(err);
    }
  };

  const isUpcomingNext30 = (timeSlot) => {
    if (!timeSlot) return false;
    const start = timeSlot.split("-")[0]?.trim();
    if (!start) return false;
    try {
      const dt = parse(`${dateStr} ${start}`, "dd.MM.yyyy HH:mm", new Date());
      const diff = (dt.getTime() - now.getTime()) / 60000;
      return diff >= 0 && diff <= 30;
    } catch {
      return false;
    }
  };

  const isToday = fmtDe(new Date()) === dateStr;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sticky header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/staff")}
            className="p-2 -ml-2 text-slate-600 active:bg-slate-100 rounded-lg"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="text-base font-semibold text-slate-900 leading-tight"
              style={{ fontFamily: "Playfair Display" }}
            >
              Tagesplan
            </h1>
            <p className="text-xs text-slate-500 truncate">
              {format(date, "EEEE, d. MMMM", { locale: de })}
              {isToday && <span className="text-sky-600 font-medium"> · heute</span>}
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 text-slate-500 active:bg-slate-100 rounded-lg"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {/* Date nav */}
        <div className="px-4 pb-3 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate(addDays(date, -1))}
            className="flex-shrink-0"
            data-testid="prev-day-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant={isToday ? "default" : "outline"}
            size="sm"
            onClick={() => setDate(new Date())}
            className={`flex-1 ${isToday ? "bg-sky-500 hover:bg-sky-600" : ""}`}
            data-testid="today-btn"
          >
            Heute
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate(addDays(date, 1))}
            className="flex-shrink-0"
            data-testid="next-day-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 p-4">
        <StatTile
          label="Sessions"
          value={data?.eisstock?.total_groups ?? 0}
          sub={`${data?.eisstock?.checked_in_groups ?? 0} eingecheckt`}
          color="bg-sky-50 text-sky-700"
        />
        <StatTile
          label="Personen"
          value={data?.eisstock?.total_persons ?? 0}
          sub="Eisstock heute"
          color="bg-emerald-50 text-emerald-700"
        />
      </div>

      {/* Sessions list */}
      <main className="px-4 pb-24 space-y-2">
        {!data || data.eisstock.bookings.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center text-slate-500 text-sm">
            Keine Buchungen für diesen Tag.
          </div>
        ) : (
          data.eisstock.bookings.map((b) => {
            const upcoming = isToday && isUpcomingNext30(b.time_slot) && !b.checked_in;
            return (
              <div
                key={b.id}
                className={`rounded-2xl p-4 shadow-sm border ${
                  upcoming
                    ? "bg-amber-50 border-amber-200"
                    : "bg-white border-slate-100"
                }`}
                data-testid={`row-${b.id}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      upcoming
                        ? "bg-amber-200 text-amber-900"
                        : b.checked_in
                        ? "bg-green-100 text-green-700"
                        : "bg-sky-100 text-sky-700"
                    }`}
                  >
                    <Clock className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <span className="font-semibold text-slate-900">{b.time_slot}</span>
                      {b.checked_in ? (
                        <Badge className="bg-green-100 text-green-800 text-[10px] gap-0.5 py-0.5 px-2">
                          <CheckCircle2 className="w-3 h-3" />
                          OK
                        </Badge>
                      ) : (
                        <Badge className="bg-sky-100 text-sky-800 text-[10px] py-0.5 px-2">
                          Offen
                        </Badge>
                      )}
                    </div>
                    <div className="font-medium text-slate-900 truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 flex flex-wrap gap-3 mt-1">
                      <a href={`tel:${b.phone}`} className="flex items-center gap-1 active:text-sky-600">
                        <Phone className="w-3 h-3" /> {b.phone}
                      </a>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" /> {b.group_size} Pers.
                      </span>
                    </div>
                    {upcoming && (
                      <div className="mt-2 flex items-center gap-1 text-amber-700 text-xs font-medium">
                        <AlertCircle className="w-3.5 h-3.5" />
                        in &lt; 30 Min
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}

        {/* PDF button */}
        <Button
          onClick={downloadPdf}
          className="w-full h-12 mt-4 bg-sky-500 hover:bg-sky-600 rounded-xl"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Als PDF speichern
        </Button>

        <p className="text-center text-[10px] text-slate-400 pt-3">
          Auto-Update alle 30 Sek · Stand: {format(now, "HH:mm")}
        </p>
      </main>
    </div>
  );
};

const StatTile = ({ label, value, sub, color }) => (
  <div className={`rounded-2xl p-4 ${color}`}>
    <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-3xl font-bold mt-1">{value}</div>
    <div className="text-xs opacity-70 mt-0.5">{sub}</div>
  </div>
);

export default StaffTagesplan;
